import { Edit, Trash, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import Button from '../components/Common/Button';
import CommonDialog from '../components/Common/Dialog';
import AddStudent from '../components/CoOrdinator/AddStudent';
import DeleteStudentDialog from '../components/CoOrdinator/DeleteStudentDialog';
import EditStudentDialog from '../components/CoOrdinator/EditStudentDialog';
import ImportStudentDialog from '../components/ImportStudents';
import Pagination from '../components/ReUsableComponents/CMS/Pagination';
import {
  useDeleteStudentProfileMutation,
  useLazyFetchStudentProfileQuery,
  useLazyGetAllStudentsAuditsQuery,
  useLazySendVerificationMailQuery
} from '../services/CoOrdinatorApi/coOrdinatorApi';
import { cn } from '../utils/tailwindMerge';

export type Student = {
  id: string;
  isActive: boolean;
  createdDate: string;
  updatedDate: string | null;
  studentName: string;
  studentEmail: string;
  college: string | null;
  isProfileCompleted: boolean | null;
  yearOfPassing: number | null;
  mobileNumber: string;
  interestedCompanies: string[];
  profilePic: string | null;
  lastLoginDate: string | null;
  currentYearOfStudy: number;
  department: string;
  qualification: string;
  yearOfCommencement: number;
};

export type FormData = {
  studentName: string;
  studentEmail: string;
  mobileNumber: string;
};

const StudentRecords: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [getStudents, { data, isLoading }] = useLazyGetAllStudentsAuditsQuery();
  const [openStudentEditDialog, setOpenStudentEditDialog] = useState(false);
  const [openStudentDeleteDialog, setOpenStudentDeleteDialog] = useState(false);
  const [sendVerificationMail, { isFetching: isResending }] =
    useLazySendVerificationMailQuery();
  const [resendLoadingId, setResendLoadingId] = useState<string | null>(null);

  const [fetchStudentProfile] = useLazyFetchStudentProfileQuery();
  const [student, setStudent] = useState({} as Student);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  //const isFirstRender = React.useRef(true);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchStudents = async () => {
    try {
      await getStudents({
        page,
        size,
        search: debouncedSearch || '',
        sortBy: sortBy || 'createdDate',
        sortOrder: sortOrder || 'desc'
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageChange = (e: number) => {
    setPage(e);
  };
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    //if (isFirstRender.current) {
    //isFirstRender.current = false;
    //return;
    //}

    getStudents({
      page,
      size,
      search: debouncedSearch || '',
      sortBy: sortBy || 'createdDate',
      sortOrder: sortOrder || 'desc'
    });
  }, [page, size, debouncedSearch, sortBy, sortOrder]);

  const usedPercentage = data?.seatsAllocated
    ? Math.round((data.studentsCount / data.seatsAllocated) * 100)
    : 0;
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // wait 500ms

    return () => {
      clearTimeout(handler);
    };
  }, [search]);
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const coOrdinatorUpgradePopUp = () => {
    return (
      <div className='rounded-2xl bg-gray-50 p-6 text-center shadow-sm'>
        <div className='flex flex-col items-center'>
          <p className='mb-2 text-lg font-semibold text-gray-800'>
            You’ve reached your plan limit.
          </p>
          <p className='text-sm text-gray-600'>
            Please contact{' '}
            <a
              href='mailto:partnerships@fresherstoday.com'
              className='font-medium text-blue-600 hover:underline'
            >
              partnerships@fresherstoday.com
            </a>{' '}
            for plan upgrades or assistance.
          </p>
        </div>
      </div>
    );
  };

  const handleResendVerification = async (studentId?: string | null) => {
    if (!studentId?.trim?.()) {
      console.error('Invalid student ID');
      return;
    }

    try {
      setResendLoadingId(studentId);
      await sendVerificationMail(studentId).unwrap();
      toast.success('Verification mail sent!');
    } catch (error) {
      toast.error('Failed to send verification mail');
    } finally {
      setResendLoadingId(null);
    }
  };

  const [deleteStudentProfile] = useDeleteStudentProfileMutation();

  const onConfirm = async () => {
    try {
      await deleteStudentProfile(student?.id).unwrap();
      setOpenStudentDeleteDialog(false);
      toast.success('Updated Successfully');
    } catch (error) {
      console.log(error, 'error13');
      toast.error('Something went wrong. Please try again.');
    }
  };
  const sortedStudents = React.useMemo(() => {
    if (!data?.data) return [];

    return [...data.data].sort((a, b) => {
      if (!sortBy) return 0;

      const valA = a[sortBy as keyof Student];
      const valB = b[sortBy as keyof Student];

      // Date sorting
      if (sortBy.includes('Date')) {
        const dateA = valA ? new Date(valA as string).getTime() : 0;
        const dateB = valB ? new Date(valB as string).getTime() : 0;
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // String sorting
      return sortOrder === 'asc'
        ? String(valA).localeCompare(String(valB), undefined, {
            sensitivity: 'base'
          })
        : String(valB).localeCompare(String(valA), undefined, {
            sensitivity: 'base'
          });
    });
  }, [data, sortBy, sortOrder]);

  return (
    <div className='h-full bg-gradient-to-br from-gray-50 to-gray-100 p-6'>
      <div className='mx-auto flex h-full max-w-7xl flex-col justify-between'>
        {/* Header Section */}
        <div>
          <div className='mb-6'>
            <div className='flex flex-wrap items-center justify-between gap-4'>
              <h1 className='text-3xl font-bold text-gray-900'>
                Manage Students
              </h1>

              {/* Action Buttons */}
              <div className='flex gap-3'>
                <Button
                  handleClickBtn={() => setIsImportDialogOpen(true)}
                  label='Import'
                  variant='contained'
                />
                <Button
                  label='+ Add New Student'
                  variant='contained'
                  handleClickBtn={() => setIsDialogOpen(true)}
                />
              </div>
            </div>

            <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
              {/* Total Students */}
              <div className='rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-200'>
                <p className='text-sm font-medium text-gray-600'>
                  Total Students
                </p>
                <p className='mt-1 text-3xl font-bold text-gray-900'>
                  {data?.studentsCount || 0}
                </p>
              </div>

              {/* Allocated Seats */}
              <div className='rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-200'>
                <p className='text-sm font-medium text-gray-600'>
                  Allocated Seats
                </p>
                <p className='mt-1 text-3xl font-bold text-gray-900'>
                  {data?.seatsAllocated || 0}
                </p>
              </div>

              {/* Seat Utilization */}
              <div className='rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-200'>
                <p className='text-sm font-medium text-gray-600'>
                  Seat Utilization
                </p>
                <p className='mt-1 text-3xl font-bold text-gray-900'>
                  {usedPercentage}%
                </p>

                <div className='mt-3'>
                  <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200'>
                    <div
                      className='h-full rounded-full bg-green-500 transition-all duration-500'
                      style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEARCH BAR BELOW TOTAL STUDENTS */}
            <div className='mt-4 w-full max-w-sm'>
              <input
                type='text'
                placeholder='Search by name or email'
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className='w-full rounded-md border border-gray-300 px-4 py-3 text-sm text-gray-800 placeholder-gray-500 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
              />
            </div>

            {/* Table Section */}
            <div className='overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 '>
              <div className='overflow-x-auto'>
                {isLoading ? (
                  <div className='flex items-center justify-center py-12'>
                    <div className='h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600'></div>
                  </div>
                ) : data?.data?.length === 0 ? (
                  <div className='py-12 text-center'>
                    <svg
                      className='mx-auto h-12 w-12 text-gray-400'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4'
                      />
                    </svg>
                    <h3 className='mt-2 text-sm font-medium text-gray-900'>
                      No students found
                    </h3>
                    <p className='mt-1 text-sm text-gray-500'>
                      Get started by adding a new student.
                    </p>
                  </div>
                ) : (
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        <th
                          onClick={() => handleSort('studentName')}
                          className='cursor-pointer px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'
                        >
                          <div className='flex items-center gap-1'>
                            Name
                            {sortBy === 'studentName' ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp size={16} className='text-gray-700' />
                              ) : (
                                <ArrowDown
                                  size={16}
                                  className='text-gray-700'
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                size={16}
                                className='text-gray-500'
                              />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('studentEmail')}
                          className='cursor-pointer px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'
                        >
                          <div className='flex items-center gap-1'>
                            Email
                            {sortBy === 'studentEmail' ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp size={16} className='text-gray-700' />
                              ) : (
                                <ArrowDown
                                  size={16}
                                  className='text-gray-700'
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                size={16}
                                className='text-gray-500'
                              />
                            )}
                          </div>
                        </th>
                        <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'>
                          Mobile
                        </th>
                        <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'>
                          Profile Status
                        </th>
                        <th
                          onClick={() => handleSort('createdDate')}
                          className='cursor-pointer whitespace-nowrap px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'
                        >
                          <div className='flex items-center gap-1'>
                            Created Date
                            {sortBy === 'createdDate' ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp size={16} className='text-gray-700' />
                              ) : (
                                <ArrowDown
                                  size={16}
                                  className='text-gray-700'
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                size={16}
                                className='text-gray-500'
                              />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort('lastLoginDate')}
                          className='cursor-pointer whitespace-nowrap px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'
                        >
                          <div className='flex items-center gap-1'>
                            Last Login
                            {sortBy === 'lastLoginDate' ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp size={16} className='text-gray-700' />
                              ) : (
                                <ArrowDown
                                  size={16}
                                  className='text-gray-700'
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                size={16}
                                className='text-gray-500'
                              />
                            )}
                          </div>
                        </th>
                        <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200 bg-white'>
                      {sortedStudents.map((student: Student) => (
                        <tr
                          key={student.id}
                          className='transition-colors hover:bg-gray-50'
                        >
                          <td className='px-6 py-4'>
                            <div className='flex items-center'>
                              <div className='ml-4'>
                                <div className='text-sm font-semibold text-gray-900'>
                                  {student.studentName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className='px-6 py-4 text-sm text-gray-600'>
                            {student.studentEmail}
                          </td>
                          <td className='px-6 py-4 text-sm text-gray-600'>
                            {student.mobileNumber}
                          </td>
                          <td className='px-6 py-4'>
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                student.isProfileCompleted
                                  ? 'bg-green-100 text-green-800 ring-1 ring-green-600/20'
                                  : 'bg-amber-100 text-amber-800 ring-1 ring-amber-600/20'
                              }`}
                            >
                              <span
                                className={`mr-1.5 h-1.5 w-1.5 rounded-full ${
                                  student.isProfileCompleted
                                    ? 'bg-green-600'
                                    : 'bg-amber-600'
                                }`}
                              ></span>
                              {student.isProfileCompleted
                                ? 'Completed'
                                : 'Pending'}
                            </span>
                          </td>
                          <td className='px-6 py-4 text-sm text-gray-600'>
                            {new Date(
                              `${student?.createdDate}z`
                            )?.toLocaleString()}
                          </td>
                          <td className='px-6 py-4 text-sm text-gray-600'>
                            {student?.lastLoginDate
                              ? new Date(
                                  `${student?.lastLoginDate}z`
                                )?.toLocaleString()
                              : 'N/A'}
                          </td>
                          <td className='px-6 py-4 text-sm text-gray-600'>
                            <div
                              className={cn(
                                student.isProfileCompleted
                                  ? 'items-center justify-end'
                                  : 'justify-end',
                                'flex '
                              )}
                            >
                              {student.isProfileCompleted && (
                                <button
                                  onClick={() => {
                                    setStudent(student);
                                    fetchStudentProfile(student?.id);
                                    setOpenStudentEditDialog(true);
                                  }}
                                  className='rounded-xl p-1 text-blue-600 transition-all duration-200 hover:bg-blue-100 hover:text-blue-700'
                                >
                                  <Edit size={20} strokeWidth={2} />
                                </button>
                              )}
                              {/* RESEND BUTTON */}
                              {!student?.isProfileCompleted && (
                                <button
                                  disabled={resendLoadingId === student?.id}
                                  onClick={() =>
                                    handleResendVerification(student?.id)
                                  }
                                  className={cn(
                                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition-all duration-200',
                                    resendLoadingId === student?.id
                                      ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                                      : 'bg-blue-100 text-blue-800 ring-1 ring-blue-600/20 hover:bg-blue-200'
                                  )}
                                >
                                  <span
                                    className={cn(
                                      'mr-1.5 h-1.5 w-1.5 rounded-full',
                                      resendLoadingId === student?.id
                                        ? 'bg-gray-500'
                                        : 'bg-blue-600'
                                    )}
                                  ></span>
                                  {resendLoadingId === student?.id
                                    ? 'Sending...'
                                    : 'Resend'}
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setStudent(student);
                                  setOpenStudentDeleteDialog(true);
                                }}
                                className='rounded-xl p-1 text-red-600 transition-all duration-200 hover:bg-red-100 hover:text-red-700'
                              >
                                <Trash size={20} strokeWidth={2} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
          {/* Pagination */}
          {data?.data?.length !== 0 && (
            <div className='mt-6'>
              <Pagination
                currentPage={page}
                handlePageChange={handlePageChange}
                handlePageSizeChange={e => {
                  setPage(0);
                  setSize(+e.target.value);
                }}
                pageSize={size}
                totalquestions={data?.studentsCount || 0}
              />
            </div>
          )}
        </div>
        <ImportStudentDialog
          setIsImportDialogOpen={setIsImportDialogOpen}
          isImportDialogOpen={isImportDialogOpen}
        />
        <CommonDialog
          close={() => setIsDialogOpen(false)}
          isOpen={isDialogOpen}
          dialogTitle={'Add New Student'}
          width='md:w-1/2'
          childClasses='max-h-[90vh] overflow-y-auto'
        >
          {data?.studentsCount >= data?.seatsAllocated ? (
            coOrdinatorUpgradePopUp()
          ) : (
            <AddStudent
              setIsDialogOpen={setIsDialogOpen}
              fetchStudents={fetchStudents}
            />
          )}
        </CommonDialog>
        <EditStudentDialog
          student={student}
          setIsOpen={setOpenStudentEditDialog}
          isOpen={openStudentEditDialog}
        />
        <DeleteStudentDialog
          studentName={student?.studentName}
          isOpen={openStudentDeleteDialog}
          onClose={() => setOpenStudentDeleteDialog(false)}
          onDelete={onConfirm}
          title='Delete Student'
        />
      </div>
    </div>
  );
};

export default StudentRecords;
